import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function ReturnRequestScreen() {
  const {
    currentParams,
    navigate
  } = useNavigation();
  const {
    orders
  } = useApp();
  const orderId = currentParams?.orderId;
  const order = orders.find(o => o.id === orderId) || orders[0];
  const [step, setStep] = useState(1);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  if (!order) return null;
  const reasons = ['Defective/Damaged', 'Wrong Item Received', 'Item Not as Described', 'Changed My Mind', 'Other'];
  const toggleItem = (itemId: string) => {
    setSelectedItems(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };
  const getRefundTotal = () => {
    return order.items.filter(item => selectedItems.includes(item.id)).reduce((total, item) => total + item.product.price * item.quantity, 0);
  };
  const handleSubmit = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setTimeout(() => {
        navigate(Screen.ORDERS);
      }, 2000);
    }, 1500);
  };
  if (success) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center" data-id="element-1263">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15
      }} className="mb-6 text-success" data-id="element-1264">
          <CheckCircle2 className="w-24 h-24" data-id="element-1265" />
        </motion.div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1266">
          Return Request Submitted
        </h1>
        <p className="text-gray-500 mb-6" data-id="element-1267">
          We've received your request. You will get an email with return
          instructions shortly.
        </p>
        <Button onClick={() => navigate(Screen.ORDERS)} data-id="element-1268">Back to Orders</Button>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-1269">
      <Header title="Request Return" data-id="element-1270" />

      <div className="p-4 space-y-4" data-id="element-1271">
        {/* Progress Indicator */}
        <div className="flex justify-between items-center mb-6 px-4" data-id="element-1272">
          {[1, 2, 3].map(s => <div key={s} className="flex flex-col items-center gap-1 relative z-10" data-id="element-1273">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= s ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1274">
                {s}
              </div>
              <span className={`text-[10px] font-medium ${step >= s ? 'text-primary' : 'text-gray-400'}`} data-id="element-1275">
                {s === 1 ? 'Items' : s === 2 ? 'Reason' : 'Details'}
              </span>
            </div>)}
          <div className="absolute left-12 right-12 top-8 h-1 bg-gray-200 -z-0" data-id="element-1276">
            <div className="h-full bg-primary transition-all duration-300" style={{
            width: `${(step - 1) / 2 * 100}%`
          }} data-id="element-1277" />
          </div>
        </div>

        {step === 1 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-1278">
            <h2 className="font-bold text-gray-900 text-lg" data-id="element-1279">
              Select items to return
            </h2>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1280">
              Choose the items from order #{order.id} you wish to return.
            </p>

            {order.items.map(item => <label key={item.id} className={`flex gap-3 p-4 bg-white rounded-2xl border-2 cursor-pointer transition-colors ${selectedItems.includes(item.id) ? 'border-primary bg-primary/5' : 'border-transparent shadow-sm'}`} data-id="element-1281">
                <div className="pt-1" data-id="element-1282">
                  <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => toggleItem(item.id)} className="w-5 h-5 rounded text-primary focus:ring-primary" data-id="element-1283" />
                </div>
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0" data-id="element-1284">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-1285" />
                </div>
                <div className="flex-1" data-id="element-1286">
                  <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1" data-id="element-1287">
                    {item.product.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-1" data-id="element-1288">
                    Qty: {item.quantity}
                  </p>
                  <p className="text-sm font-bold text-primary" data-id="element-1289">
                    ₦{(item.product.price * item.quantity).toLocaleString()}
                  </p>
                </div>
              </label>)}
          </motion.div>}

        {step === 2 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-1290">
            <h2 className="font-bold text-gray-900 text-lg" data-id="element-1291">
              Why are you returning this?
            </h2>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden" data-id="element-1292">
              {reasons.map((r, idx) => <label key={r} className={`flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-gray-50 ${idx !== reasons.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-1293">
                  <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} className="w-5 h-5 text-primary focus:ring-primary" data-id="element-1294" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-1295">{r}</span>
                </label>)}
            </div>
          </motion.div>}

        {step === 3 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-1296">
            <h2 className="font-bold text-gray-900 text-lg" data-id="element-1297">
              Additional Details
            </h2>

            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1298">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1299">
                Please provide more information (Optional)
              </label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the issue in detail..." className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none h-32" data-id="element-1300" />
            </div>

            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1301">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-1302">Refund Summary</h3>
              <div className="space-y-2 text-sm" data-id="element-1303">
                <div className="flex justify-between text-gray-600" data-id="element-1304">
                  <span data-id="element-1305">Refund Amount</span>
                  <span className="font-medium text-gray-900" data-id="element-1306">
                    ₦{getRefundTotal().toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600" data-id="element-1307">
                  <span data-id="element-1308">Refund Method</span>
                  <span className="font-medium text-gray-900" data-id="element-1309">
                    Original Payment
                  </span>
                </div>
                <div className="h-px bg-gray-100 my-2" data-id="element-1310"></div>
                <div className="flex items-start gap-2 text-xs text-gray-500 mt-2" data-id="element-1311">
                  <AlertCircle className="w-4 h-4 text-primary shrink-0" data-id="element-1312" />
                  <p data-id="element-1313">
                    Refund will be processed within 5-7 business days after we
                    receive the returned items.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-1314">
        <div className="max-w-md mx-auto flex gap-3" data-id="element-1315">
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1" data-id="element-1316">
              Back
            </Button>}

          {step < 3 ? <Button className="flex-[2]" onClick={() => setStep(step + 1)} disabled={step === 1 && selectedItems.length === 0 || step === 2 && !reason} data-id="element-1317">
              Continue
            </Button> : <Button className="flex-[2]" onClick={handleSubmit} loading={loading} data-id="element-1318">
              Submit Request
            </Button>}
        </div>
      </div>
    </motion.div>;
}